import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { INDIAN_STATES, stateNameForCode } from '@/lib/indianStates';
import type { CompanyDto, UpdateCompanyRequest } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().min(1, t('form.validation.nameRequired')),
    cin: z.string().nullable(),
    gstin: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    parentCompanyName: z.string().nullable(),
    industrySector: z.string().nullable(),
    logoUrl: z.string().nullable(),
    fiscalYearStartMonth: z.coerce.number().min(1).max(12),
    fiscalYearStartDay: z.coerce.number().min(1).max(31),
    currency: z.string().min(1),
    taxSystem: z.string().min(1),
    status: z.enum(['Active', 'Inactive']),
    stateCode: z.string().nullable(),
    stateName: z.string().nullable(),
  });
}

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

interface CompanyFormDialogProps {
  open: boolean;
  company: CompanyDto | null;
  onClose: () => void;
  onSubmit: (values: UpdateCompanyRequest) => void;
  loading?: boolean;
}

export default function CompanyFormDialog({ open, company, onClose, onSubmit, loading }: CompanyFormDialogProps) {
  const { t } = useTranslation('companyMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      cin: null,
      gstin: null,
      email: null,
      website: null,
      parentCompanyName: null,
      industrySector: null,
      logoUrl: null,
      fiscalYearStartMonth: 4,
      fiscalYearStartDay: 1,
      currency: 'INR',
      taxSystem: 'GST',
      status: 'Active',
      stateCode: null,
      stateName: null,
    },
  });

  useEffect(() => {
    if (open && company) reset({ ...company, status: company.status as 'Active' | 'Inactive' });
  }, [open, company, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('editCompany')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={2}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} value={field.value ?? ''} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} sx={{ mt: 1 }} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="cin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.cin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="gstin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.gstin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="email" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.email')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="website" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.website')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="parentCompanyName" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.parentCompanyName')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="industrySector" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.industrySector')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="fiscalYearStartMonth" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.fiscalYearStartMonth')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="fiscalYearStartDay" control={control} render={({ field }) => <TextField {...field} type="number" label={t('form.fiscalYearStartDay')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="currency" control={control} render={({ field }) => <TextField {...field} label={t('form.currency')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="taxSystem" control={control} render={({ field }) => <TextField {...field} label={t('form.taxSystem')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="stateCode"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label={t('form.state')}
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const code = e.target.value || null;
                    field.onChange(code);
                    setValue('stateName', stateNameForCode(code));
                  }}
                >
                  <MenuItem value="">{t('form.stateNone')}</MenuItem>
                  {INDIAN_STATES.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </GridItem>
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
